import formatRoute from '@src/layout-components/route/format-route';
import matchPath from '@src/layout-components/route/route-switch-enhance/match-path';
import RouteEnhance from '@src/layout-components/route/route-enhance';

/**
 * 路由配置
 * 参数如 path、exact 等请参考 https://reacttraining.com/react-router/web/example/basic
 *
 * {Function} asyncRender: 异步 render 函数，返回 promise component，例如 import('../main')
 * {Function} getFormatPath: 生成真实跳转路径的方法
 */
const routeConfigs = {
  /**
   * 首页
   */
  MAIN: {
    path: '/',
    // native: true, // 开启后直接使用默认路由，不进行预加载、loading 和切换入场动画
    // animate: false, // 禁用页面入场动画(默认右边滑到左边)
    // useLoading: false, // 是否使用 loading，默认开启
    exact: true,
    asyncRender: () => import(/* webpackChunkName: "page-main", webpackPrefetch: true */ '../main'),
    getFormatPath: () => formatRoute(routeConfigs.MAIN.path)
  },
  /**
   * 内页列表页（单路由 + switch 渲染各内页）
   * 规则参考: /list/11935（id 为文章 articleId，借鉴上一届 articleId 路由管理）
   */
  LIST: {
    path: '/list/:id',
    exact: true,
    asyncRender: () => import(/* webpackChunkName: "page-list" */ '../list'),
    getFormatPath: () => formatRoute(routeConfigs.LIST.path)
  },
  /**
   * 降级命中页，常作 404 使用
   */
  FALLBACK: {
    path: '**',
    /**
     * 这里使用 "~defer" 代表不进行 prefetch
     * @returns {Promise<*>|*}
     */
    asyncRender: () => import(/* webpackChunkName: "page-not-found~defer" */ '../not-found')
  }
};

/**
 * 获取 match 匹配路由
 * @param {string} actualPath - 实际地址，例如 /person/detail?id=123
 * @returns {string|null}
 */
routeConfigs.match = function(actualPath) {
  if (!/^\/[^/].*$/.test(actualPath)) {
    return null;
  }
  const [pathname, query] = actualPath.split('?');
  const matchKey = Object.keys(routeConfigs).find(key => {
    return !!matchPath({ pathname, query }, routeConfigs[key]);
  });
  return routeConfigs[matchKey] || null;
};

/**
 * 禁止遍历
 */
Object.defineProperty(routeConfigs, 'match', { enumerable: false });

/**
 * 预加载 e.g:
 * routeConfigs.prefetch('/person/detail?id=123')
 * routeConfigs.prefetch({ actualPath: '/person/detail?id=123', prefetchUntilLoadAsyncComponent: true })
 *
 * @param {string|{actualPath,prefetchUntilLoadAsyncComponent}} actualPath - 实际地址，例如 /person/detail?id=123
 * @returns {Promise<*>|undefined}
 */
routeConfigs.prefetch = actualPath => {
  if (typeof actualPath === 'string') {
    const routeConfig = routeConfigs.match(actualPath);
    if (routeConfig && routeConfig.asyncRender) {
      return RouteEnhance.prefetch({ asyncRender: routeConfig.asyncRender });
    }
    return undefined;
  }
  /**
   * @type {{actualPath,prefetchUntilLoadAsyncComponent}}
   */
  const options = actualPath;
  const routeConfig = routeConfigs.match(options.actualPath);
  if (routeConfig && routeConfig.asyncRender) {
    return RouteEnhance.prefetch({
      asyncRender: routeConfig.asyncRender,
      prefetchUntilLoadAsyncComponent: options.prefetchUntilLoadAsyncComponent
    });
  }
  return undefined;
};

/**
 * 禁止遍历
 */
Object.defineProperty(routeConfigs, 'prefetch', { enumerable: false });

export default routeConfigs;
